import { Bet } from "../../types/Bet";
import { Match } from "../../types/Match";

/**
 * Filtering model for the Bets page. Each category is multi-select; within a
 * category the selected values are OR-ed, and the categories are AND-ed.
 * An empty category applies no constraint.
 */
export interface BetFilters {
  status: string[];
  outcome: string[];
  stage: string[];
  teams: string[];
}

export const emptyBetFilters: BetFilters = {
  status: [],
  outcome: [],
  stage: [],
  teams: [],
};

export const STATUS_OPTIONS = ["Upcoming", "Finished"];
export const OUTCOME_OPTIONS = ["Perfect", "Correct", "Wrong", "Pending"];
export const STAGE_OPTIONS = [
  "Group Stage",
  "Round of 16",
  "Quarter-finals",
  "Semi-finals",
  "Final",
];

export function betFiltersActive(filters: BetFilters): boolean {
  return (
    filters.status.length > 0 ||
    filters.outcome.length > 0 ||
    filters.stage.length > 0 ||
    filters.teams.length > 0
  );
}

// betResult mirrors the backend BetOutcome enum: 0 = wrong, 1 = correct
// (right outcome), 2 = perfect (exact score). Matches BetOutcomeIcon/ShowPoints.
// A bet is only settled once its match is played; until then it is "Pending".
// Settlement keys off match.wasPlayed (the same signal BetCard uses to show the
// result) rather than bet.isActive, which only tracks whether the bet is still
// editable and can stay true after the match has been played.
function outcomeLabel(bet: Bet, match: Match | undefined): string {
  if (!match?.wasPlayed) return "Pending";
  if (bet.betResult === 1) return "Correct";
  if (bet.betResult === 2) return "Perfect";
  return "Wrong";
}

// "Group Stage" buckets all group matches; knockout matches use their round name.
function stageLabel(match: Match | undefined): string | undefined {
  if (!match) return undefined;
  return match.matchType.type === "Group" ? "Group Stage" : match.matchType.value;
}

/** Distinct team names across the matches the user has bet on, sorted A→Z. */
export function deriveTeamOptions(bets: Bet[], matches: Match[]): string[] {
  const matchById = new Map(matches.map((match) => [match.id, match]));
  const names = new Set<string>();
  bets.forEach((bet) => {
    const match = matchById.get(bet.matchId);
    if (match?.homeTeam?.internationalName) names.add(match.homeTeam.internationalName);
    if (match?.awayTeam?.internationalName) names.add(match.awayTeam.internationalName);
  });
  return Array.from(names).sort((a, b) => a.localeCompare(b));
}

export function applyBetFilters(
  bets: Bet[],
  matches: Match[],
  filters: BetFilters
): Bet[] {
  const matchById = new Map(matches.map((match) => [match.id, match]));
  return bets.filter((bet) => {
    const match = matchById.get(bet.matchId);

    if (filters.status.length) {
      const label = match?.wasPlayed ? "Finished" : "Upcoming";
      if (!filters.status.includes(label)) return false;
    }

    if (
      filters.outcome.length &&
      !filters.outcome.includes(outcomeLabel(bet, match))
    ) {
      return false;
    }

    if (filters.stage.length) {
      const stage = stageLabel(match);
      if (!stage || !filters.stage.includes(stage)) return false;
    }

    if (filters.teams.length) {
      const home = match?.homeTeam?.internationalName;
      const away = match?.awayTeam?.internationalName;
      const matchesTeam =
        (!!home && filters.teams.includes(home)) ||
        (!!away && filters.teams.includes(away));
      if (!matchesTeam) return false;
    }

    return true;
  });
}
